import { useEffect } from 'react';
import { X, ImageOff, RotateCcw, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import type { Exercise, PlanEntry } from '../../types/exercise';
import { LwsBadge } from './LwsBadge';
import { BandColorChip } from './BandColorChip';
import { SetTracker } from './SetTracker';
import { ExerciseIllustration } from './ExerciseIllustration';
import { useExerciseProgress } from '../../lib/useSetProgress';
import { showToast } from '../../lib/toast';

interface ExerciseDetailProps {
  exercise: Exercise;
  /** Wenn gesetzt → Plan-Kontext (Sets-Tracker aktiv). Wenn weggelassen → Library-Kontext. */
  entry?: PlanEntry | undefined;
  dayKey: string;
  onClose: () => void;
  /** Plan-Kontext: nächsten Plan-Eintrag öffnen oder Sheet schließen wenn letzte. */
  onAdvance?: () => void;
  /** Plan-Kontext: gibt es eine nächste Übung im Plan? */
  hasNext?: boolean;
}

const EQUIPMENT_LABEL: Record<string, string> = {
  plattform: 'Plattform',
  matte: 'Matte',
  frei: 'Frei',
  curlbar: 'Curlbar',
  griffe: 'Griffe',
  fussschlaufen: 'Fussschlaufen',
  tueranker: 'Tueranker',
  'kurzes-band': 'Kurzes Band',
  'langes-band': 'Langes Band',
};

export function ExerciseDetail({
  exercise,
  entry,
  dayKey,
  onClose,
  onAdvance,
  hasNext = false,
}: ExerciseDetailProps): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const lwsVariant = exercise.lwsNote ? 'caution' : 'default';
  const inPlan = entry !== undefined;
  const setsLabel = entry ? `${entry.sets} × ${entry.reps}` : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exercise-detail-title"
      className="fixed inset-0 z-50 flex flex-col bg-zinc-50"
    >
      <header
        className="sticky top-0 z-10 border-b border-zinc-200 bg-zinc-50/95 backdrop-blur"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex h-14 items-center gap-2 px-4">
          <h1
            id="exercise-detail-title"
            className="min-w-0 flex-1 truncate text-base font-semibold text-zinc-900"
          >
            {exercise.name}
          </h1>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schliessen"
            className="-mr-2 inline-flex size-11 items-center justify-center rounded-lg text-zinc-900 active:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-6 md:px-6 md:py-8">
          <div className="mb-4 overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100 text-zinc-500">
            {exercise.imagePath ? (
              <img
                src={exercise.imagePath}
                alt={`${exercise.name} — Bewegungsausfuehrung`}
                className="block h-auto w-full"
              />
            ) : exercise.iconKey ? (
              <div className="flex aspect-[16/9] w-full items-center justify-center">
                <ExerciseIllustration iconKey={exercise.iconKey} size="hero" />
              </div>
            ) : (
              <div className="flex aspect-[16/9] w-full flex-col items-center justify-center gap-2 p-6 text-center">
                <ImageOff aria-hidden="true" className="size-8" />
                <span className="text-sm">
                  Bild folgt — Anleitung steht trotzdem zur Verfuegung.
                </span>
              </div>
            )}
          </div>

          <h2 className="text-2xl font-semibold leading-tight text-zinc-900">
            {exercise.name}
          </h2>
          <p className="mt-1 text-sm text-zinc-600">{exercise.muscles}</p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <LwsBadge variant={lwsVariant} note={exercise.lwsNote} />
            {exercise.equipment.map((eq) => (
              <span
                key={eq}
                className="inline-flex h-6 items-center rounded-full border border-zinc-200 bg-white px-2 text-xs font-semibold text-zinc-700"
              >
                {EQUIPMENT_LABEL[eq] ?? eq}
              </span>
            ))}
          </div>

          {exercise.bandColor?.long || exercise.bandColor?.short ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {exercise.bandColor?.long ? (
                <BandColorChip color={exercise.bandColor.long} bandKind="long" />
              ) : null}
              {exercise.bandColor?.short ? (
                <BandColorChip color={exercise.bandColor.short} bandKind="short" />
              ) : null}
            </div>
          ) : null}

          {exercise.setupNote ? (
            <p className="mt-4 rounded-lg bg-zinc-100 p-3 text-sm leading-normal text-zinc-700">
              <span className="font-semibold text-zinc-900">Setup. </span>
              {exercise.setupNote}
            </p>
          ) : null}

          {inPlan && entry && setsLabel ? (
            <PlanSection
              dayKey={dayKey}
              exerciseId={exercise.id}
              entry={entry}
              setsLabel={setsLabel}
            />
          ) : (
            <section className="mt-6 rounded-lg bg-zinc-100 p-3 text-sm leading-normal text-zinc-700">
              <span className="font-semibold text-zinc-900">Vorgabe. </span>
              Wird aus dem Tagesplan uebernommen, sobald die Uebung am jeweiligen Trainingstag erscheint.
            </section>
          )}

          <section className="mt-6">
            <h3 className="text-xl font-semibold leading-tight text-zinc-900">Anleitung</h3>
            <ol className="mt-2 list-decimal space-y-2 pl-5 text-base leading-normal text-zinc-900 marker:font-semibold marker:text-zinc-600">
              {exercise.instructions.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </section>

          {exercise.tip ? (
            <aside className="mt-6 rounded-xl border border-zinc-200 border-l-4 border-l-amber-500 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-amber-900">Tipp</h3>
              <p className="mt-1 text-base leading-normal text-zinc-900">{exercise.tip}</p>
            </aside>
          ) : null}

          <div className="h-32 md:h-24" />
        </div>
      </div>

      {inPlan && entry && onAdvance ? (
        <BottomActionBar
          dayKey={dayKey}
          exerciseId={exercise.id}
          entry={entry}
          hasNext={hasNext}
          onAdvance={onAdvance}
          onClose={onClose}
        />
      ) : (
        <LibraryActionBar onClose={onClose} />
      )}
    </div>
  );
}

function LibraryActionBar({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div
      className="sticky bottom-0 z-10 border-t border-zinc-200 bg-white/95 backdrop-blur"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3 md:px-6">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 text-base font-semibold text-zinc-900 transition-transform duration-[120ms] active:scale-[0.98] active:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
        >
          <ArrowLeft aria-hidden="true" className="size-5" />
          Zurueck
        </button>
      </div>
    </div>
  );
}

interface PlanSectionProps {
  dayKey: string;
  exerciseId: string;
  entry: PlanEntry;
  setsLabel: string;
}

function PlanSection({ dayKey, exerciseId, entry, setsLabel }: PlanSectionProps): JSX.Element {
  const { done, reset } = useExerciseProgress(dayKey, exerciseId, entry.sets);
  const onResetClick = (): void => {
    if (done.length === 0) return;
    if (window.confirm('Heutigen Fortschritt fuer diese Uebung zuruecksetzen?')) {
      reset();
      showToast('Fortschritt zurueckgesetzt');
    }
  };
  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-zinc-600">Vorgabe</h3>
      <p className="text-base font-semibold tabular-nums text-zinc-900">{setsLabel}</p>
      <div className="mt-3">
        <SetTracker dayKey={dayKey} exerciseId={exerciseId} totalSets={entry.sets} />
      </div>
      {done.length > 0 ? (
        <button
          type="button"
          onClick={onResetClick}
          className="mt-3 inline-flex h-11 items-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-sm font-semibold text-rose-700 active:bg-rose-50 focus-visible:outline-2 focus-visible:outline-rose-600 focus-visible:outline-offset-2"
        >
          <RotateCcw aria-hidden="true" className="size-4" />
          Uebung zuruecksetzen
        </button>
      ) : null}
    </section>
  );
}

interface BottomActionBarProps {
  dayKey: string;
  exerciseId: string;
  entry: PlanEntry;
  hasNext: boolean;
  onAdvance: () => void;
  onClose: () => void;
}

function BottomActionBar({
  dayKey,
  exerciseId,
  entry,
  hasNext,
  onAdvance,
  onClose,
}: BottomActionBarProps): JSX.Element {
  const { done, isComplete, toggle } = useExerciseProgress(dayKey, exerciseId, entry.sets);
  const doneSet = new Set(done);

  // Naechstes ungehaktes Set fuer den Quick-Action-CTA
  const nextSet = (() => {
    for (let n = 1; n <= entry.sets; n += 1) {
      if (!doneSet.has(n)) return n;
    }
    return null;
  })();

  const handleClick = (): void => {
    if (!isComplete && nextSet !== null) {
      const willComplete = done.length + 1 >= entry.sets;
      toggle(nextSet);
      if (willComplete) {
        showToast('Uebung abgeschlossen', { variant: 'success' });
      } else {
        showToast(`Set ${nextSet} abgehakt`);
      }
      return;
    }
    // Alle Sets done → Naechste Uebung oder Training-Ende.
    // Den "Training abgeschlossen"-Toast feuert TodayView's useEffect — kein Doppel-Toast hier.
    onAdvance();
  };

  let label: string;
  let Icon = Check;
  if (!isComplete && nextSet !== null) {
    label = `Set ${nextSet} abhaken`;
    Icon = Check;
  } else if (hasNext) {
    label = 'Naechste Uebung';
    Icon = ArrowRight;
  } else {
    label = 'Training abschliessen';
    Icon = Check;
  }

  return (
    <div
      className="sticky bottom-0 z-10 border-t border-zinc-200 bg-white/95 backdrop-blur"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3 md:px-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Zurueck zum Trainingsplan"
          className="inline-flex h-12 shrink-0 items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-900 transition-transform duration-[120ms] active:scale-[0.98] active:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2 sm:px-4 sm:text-base"
        >
          <ArrowLeft aria-hidden="true" className="size-5" />
          <span className="hidden sm:inline">Zurueck</span>
        </button>
        <button
          type="button"
          onClick={handleClick}
          className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 text-base font-semibold text-white shadow-sm transition-transform duration-[120ms] active:scale-[0.98] active:bg-emerald-600 focus-visible:outline-2 focus-visible:outline-emerald-700 focus-visible:outline-offset-2"
        >
          <Icon aria-hidden="true" className="size-5" />
          {label}
        </button>
      </div>
    </div>
  );
}
